import { OpenAPISpec } from "../types";

interface OverviewProps {
  spec: OpenAPISpec;
}

export function Overview({ spec }: OverviewProps) {
  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 xl:p-12 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="mb-10 sm:mb-16 pb-10 sm:pb-16 border-b border-zinc-200">
        <span className="text-[10px] font-semibold text-zinc-400 tracking-widest mb-4 block">
          API OVERVIEW
        </span>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-zinc-900 mb-4 sm:mb-6 tracking-tight">
          {spec.info.title}
        </h1>
        <div className="flex flex-wrap items-center gap-3">
          <span className="px-3 py-1.5 bg-zinc-900 text-white text-xs font-medium tracking-wider">
            v{spec.info.version}
          </span>
          {spec.openapi && (
            <span className="px-3 py-1.5 border border-zinc-200 text-xs text-zinc-600 tracking-wider">
              OPENAPI {spec.openapi}
            </span>
          )}
          {spec.swagger && (
            <span className="px-3 py-1.5 border border-zinc-200 text-xs text-zinc-600 tracking-wider">
              SWAGGER {spec.swagger}
            </span>
          )}
        </div>

        {spec.info.description && (
          <div className="mt-8 text-zinc-600 leading-relaxed text-sm sm:text-base max-w-2xl">
            {spec.info.description.split("\n").map((paragraph, i) => (
              <p key={i} className="mb-4">
                {paragraph}
              </p>
            ))}
          </div>
        )}
      </div>

      {/* Servers Section */}
      {spec.servers && spec.servers.length > 0 && (
        <div className="mb-10 sm:mb-16">
          <h2 className="text-xs font-bold text-zinc-900 tracking-widest mb-6">
            SERVERS
          </h2>
          <div className="space-y-3">
            {spec.servers.map((server, i) => (
              <div
                key={i}
                className="border border-zinc-200 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-zinc-900 transition-colors"
              >
                <code className="text-xs sm:text-sm font-mono text-zinc-900 bg-zinc-50 px-3 py-2 border border-zinc-100 break-all">
                  {server.url}
                </code>
                {server.description && (
                  <span className="text-xs text-zinc-500 tracking-wider">
                    {server.description}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* API Info Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="border border-zinc-200 p-4">
          <div className="text-2xl sm:text-3xl font-bold text-zinc-900">
            {Object.keys(spec.paths || {}).length}
          </div>
          <div className="text-[10px] text-zinc-500 tracking-widest mt-1">ENDPOINTS</div>
        </div>
        <div className="border border-zinc-200 p-4">
          <div className="text-2xl sm:text-3xl font-bold text-zinc-900">
            {Object.keys(spec.components?.schemas || {}).length}
          </div>
          <div className="text-[10px] text-zinc-500 tracking-widest mt-1">SCHEMAS</div>
        </div>
        <div className="border border-zinc-200 p-4">
          <div className="text-2xl sm:text-3xl font-bold text-zinc-900">
            {Object.keys(spec.components?.securitySchemes || spec.securityDefinitions || {}).length}
          </div>
          <div className="text-[10px] text-zinc-500 tracking-widest mt-1">SECURITY</div>
        </div>
        <div className="border border-zinc-200 p-4">
          <div className="text-2xl sm:text-3xl font-bold text-zinc-900">
            {spec.servers?.length || 1}
          </div>
          <div className="text-[10px] text-zinc-500 tracking-widest mt-1">SERVERS</div>
        </div>
      </div>
    </div>
  );
}
